import { Inject, Injectable } from '@angular/core';

import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { BehaviorSubject } from 'rxjs';
import { ICustomLocation } from '@ess-front/shared/models';
import { TranslationService } from '@ess-front/shared/translation';

@Injectable()
export class MapService {
  isLoaded$ = new BehaviorSubject<boolean>(false);
  geoService: google.maps.Geocoder;

  constructor(
    @Inject('env') private environment: any,
    private translationService: TranslationService,
  ) {
    this.initGoogleMaps();
  }

  /**
   * Geocoding is the process of converting addresses into geographic coordinates.
   */
  async toGeoCoords(address: string): Promise<google.maps.GeocoderResult> {
    return this.geocode({ address });
  }
  /**
   * Reverse geocoding is the process of converting geographic coordinates into a human-readable address.
   */
  async toGeoAddress(location: google.maps.LatLng): Promise<google.maps.GeocoderResult> {
    return this.geocode({ location });
  }

  toCustomLocation(location: google.maps.LatLng): ICustomLocation | null {
    return !!location && location instanceof google.maps.LatLng
      ? { longitude: location.lng(), latitude: location.lat() }
      : null;
  }

  fromCustomLocation(location: ICustomLocation): google.maps.LatLng | null {
    return !!location &&
      Object.prototype.hasOwnProperty.call(location, 'latitude') &&
      Object.prototype.hasOwnProperty.call(location, 'longitude')
      ? new google.maps.LatLng(location.latitude, location.longitude)
      : null;
  }

  /***/
  async geocode(request: google.maps.GeocoderRequest): Promise<google.maps.GeocoderResult> {
    return new Promise((resolve, reject) => {
      const callback = (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus): void => {
        if (status === google.maps.GeocoderStatus.OK && results[0]) {
          resolve(results[0]);
        } else {
          reject(null);
        }
      };

      this.geoService.geocode(request, callback);
    });
  }

  private initGoogleMaps(): void {
    const language = this.translationService.getActiveLanguage();
    setOptions({ key: this.environment.googleMapsApiKey, language });
    importLibrary('geocoding').then(() => {
      this.geoService = new google.maps.Geocoder();
      this.isLoaded$.next(true);
    });
  }
}
